import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackToAlbums from '@/components/BackToAlbums';
import Layout from '@/components/Layout';
import { api } from '@/lib/apiClient';
import { Photo } from '@/types/interface';
import { useClientMount } from '@/hooks/useClientMount';

const PhotoDetail = () => {
    const { albumId, photoId } = useParams() as { albumId: string; photoId: string };
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const hasMounted = useClientMount();

    // Fetch all photos for the album
    useEffect(() => {
        if (!hasMounted) return;

        const fetchPhotos = async () => {
            try {
                const response = await api.albums.getById(Number(albumId), { limit: 500 });
                const data = response.data;
                if (data?.album?.photos) {
                    setPhotos(data.album.photos); // Update all photos for the album
                } else {
                    setError(true);
                    console.error('Failed to fetch photos - no album data');
                }
            } catch (error) {
                setError(true);
                console.error('Error fetching photos:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPhotos();
    }, [albumId, hasMounted]);

    // Don't render anything until the component has mounted on the client
    if (!hasMounted) {
        return null;
    }

    // Show loading state
    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center">
                    <p className="text-xl text-gray-400">Loading...</p>
                </div>
            </Layout>
        );
    }

    // Show error state if data is not available after loading
    if (error || photos.length === 0) {
        return (
            <Layout>
                <div className="flex items-center justify-center">
                    <p className="text-xl text-gray-400">Failed to load photos or album is empty.</p>
                </div>
            </Layout>
        );
    }

    // Handle invalid photoId
    const currentPhotoIndex = photos.findIndex((photo) => photo.id === parseInt(photoId, 10));

    // Handle invalid photoId
    if (currentPhotoIndex === -1) {
        return (
            <Layout>
                <div className="flex items-center justify-center">
                    <p className="text-xl text-gray-400">Photo is not found.</p>
                </div>
            </Layout>
        );
    }

    const currentPhoto = photos[currentPhotoIndex];
    const previousPhoto = photos[currentPhotoIndex - 1] || null;
    const nextPhoto = photos[currentPhotoIndex + 1] || null;

    return (
        <Layout>
            <div className="max-w-full flex flex-wrap justify-center sm:px-4 px-2 py-6 max-h-screen">
                {/* Current Photo with Navigation Buttons */}
                <div className="relative inline-block">
                    {/* Current Photo */}
                    <img
                        src={currentPhoto.src}
                        alt={currentPhoto.alt}
                        className="max-h-[60vh] sm:max-h-[80vh] w-auto rounded-lg shadow-lg object-contain"
                    />

                    {/* Previous Button */}
                    <Link
                        to={previousPhoto ? `/albums/${albumId}/photos/${previousPhoto.id}` : '#'}
                        className={`absolute left-0 top-1/2 transform -translate-y-1/2 text-4xl sm:text-6xl font-bold text-gray-400 hover:text-gray-50 transition ${!previousPhoto && 'opacity-50 cursor-not-allowed'
                            }`}
                        onClick={(e) => !previousPhoto && e.preventDefault()}
                    >
                        <span className="hover:scale-110 transition-transform">&lt;</span>
                    </Link>

                    {/* Next Button */}
                    <Link
                        to={nextPhoto ? `/albums/${albumId}/photos/${nextPhoto.id}` : '#'}
                        className={`absolute right-0 top-1/2 transform -translate-y-1/2 text-4xl sm:text-6xl font-bold text-gray-400 hover:text-gray-50 transition ${!nextPhoto && 'opacity-50 cursor-not-allowed'
                            }`}
                        onClick={(e) => !nextPhoto && e.preventDefault()}
                    >
                        <span className="hover:scale-110 transition-transform">&gt;</span>
                    </Link>
                </div>
            </div>
            <div className="flex items-center justify-center p-1">
                <BackToAlbums albumId={albumId} />
            </div>
        </Layout>
    );
};

export default PhotoDetail;
